import { CollapsibleTagList, ExternalLink } from '@/components/Elements';
import { BookmarkButton } from '@/components/Elements/BookmarkButton';
import { ExpandableContent } from '@/components/Elements/ExpandableContent';
import { TopicTagWithTooltip } from '@/features/analyze/components/TopicTagWithTooltip';
import {
  MetadataTag,
  TagContainer,
  ThemeTag,
} from '@/features/analyze/components/Tag';
import { useSearchResultTagFilter } from '@/features/analyze/hooks/useSearchResultTagFilter';
import { FragmentOf, graphql, readFragment } from '@/lib/graphql';
import {
  CubeIcon,
  GlobeAltIcon,
  HashtagIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import { type FacetKey } from '@/features/analyze/types';

const searchResultItemFragment = graphql(`
  fragment SearchResultItemFragment on ActiveArticle {
    code
    title
    url
    summary
    publishedAt
    country
    organizationType
    organization
    displayableTopicTags {
      lookupKey
      name
      description
      themes
      isSocialIssue
    }
    themes
    imageUrl
    isBookmarkedByCurrentUser
  }
`);

export type SearchResultProps = {
  docs: ReadonlyArray<FragmentOf<typeof searchResultItemFragment>>;
};

export const SearchResult = ({ docs }: SearchResultProps) => {
  return (
    <div className=" flex w-full flex-col gap-y-6">
      {docs.map((doc) => (
        <SearchResultItem
          key={readFragment(searchResultItemFragment, doc).code}
          data={doc}
        />
      ))}
    </div>
  );
};

SearchResult.fragments = {
  data: searchResultItemFragment,
};

const SearchResultItem = ({
  data,
}: {
  data: FragmentOf<typeof searchResultItemFragment>;
}) => {
  const item = readFragment(searchResultItemFragment, data);
  const addSearchResultTagFilter = useSearchResultTagFilter();

  const onClick = (targetFieldKey: FacetKey, clickText: string | null) => {
    if (clickText == null) return;
    addSearchResultTagFilter(targetFieldKey, clickText);
  };

  return (
    <div className="flex gap-x-6">
      <img
        src={item.imageUrl || '/images/article-no-image.jpg'}
        alt="Article Thumbnail"
        className="hidden size-40 min-h-40 min-w-40 rounded-tr-md object-cover lg:block"
        onError={(e) => (e.currentTarget.src = '/images/article-no-image.jpg')}
      />
      <div className="flex flex-1 flex-col gap-y-2 overflow-visible">
        <div className="flex items-start justify-between">
          <ExternalLink
            to={item.url}
            underline="hover"
            className="grow text-lg font-semibold md:text-xl"
          >
            {item.title}
          </ExternalLink>
          <div className="ml-2 shrink-0">
            <BookmarkButton
              code={item.code}
              title={item.title}
              url={item.url}
              isBookmarked={item.isBookmarkedByCurrentUser}
            />
          </div>
        </div>
        <TagContainer>
          {item.themes && item.themes.length > 0 && (
            <CollapsibleTagList
              items={item.themes}
              maxVisibleItems={3}
              textColor="text-rw-sub"
              renderTag={(theme) => (
                <ThemeTag
                  key={theme}
                  icon={HashtagIcon}
                  tableName={theme}
                  onClick={() => onClick('themes', theme)}
                />
              )}
            />
          )}

          {/* メタデータタグ（国・組織タイプ・組織名） */}
          {item.country && (
            <MetadataTag
              icon={GlobeAltIcon}
              text={item.country}
              onClick={() => onClick('countries', item.country)}
            />
          )}
          {item.organizationType && (
            <MetadataTag
              icon={CubeIcon}
              text={item.organizationType}
              onClick={() =>
                onClick('organizationTypes', item.organizationType)
              }
            />
          )}
          {item.organization && <MetadataTag text={item.organization} />}
          {item.displayableTopicTags &&
            item.displayableTopicTags.length > 0 && (
              <CollapsibleTagList
                items={item.displayableTopicTags}
                maxVisibleItems={10}
                textColor="text-rw-black"
                renderTag={(tag) => (
                  <TopicTagWithTooltip
                    key={tag.lookupKey}
                    name={tag.name}
                    description={tag.description}
                    themes={tag.themes}
                    isSocialIssue={tag.isSocialIssue}
                    icon={TagIcon}
                    onClick={() => onClick('topicTags', tag.lookupKey)}
                  />
                )}
              />
            )}
        </TagContainer>
        <ExpandableContent className="line-clamp-3">
          {`${new Date(item.publishedAt).toLocaleDateString()} - ${
            item.summary
          }`}
        </ExpandableContent>
      </div>
    </div>
  );
};
